import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { SettingsService } from './settings.service';
import { LoginService } from './login.service';

@Injectable({
  providedIn: 'root'
})
export class DeviceColumnService {

  columnDefinitions: any;
  currentUser: any;

  filterOps = {
    '<': (a: any, filterValue: any) => a !== null && a < filterValue,
    '<=': (a: any, filterValue: any) => a !== null && a <= filterValue,
    '>': (a: any, filterValue: any) => a !== null && a > filterValue,
    '>=': (a: any, filterValue: any) => a !== null && a >= filterValue,
    '=': (a: any, filterValue: any) => a !== null && a === filterValue
  };

  constructor(
    private settingService: SettingsService,
    private loginService: LoginService
  ) {
    this.loginService.currentUser.subscribe(x => this.currentUser = x);

    this.columnDefinitions = {
      state: this.DeviceStatusCell({
        title: 'Status'
        , value: (device) => {
          return device.enhancedStateAction
        }
      })
      , group: this.TextCell({
        title: 'Group Name'
        , value: (device) => {
          return device.group.name
        }
      })
      , groupSchedule: this.TextCell({
        title: 'Group Class'
        , value: (device) => {
          return device.group.class
        }
      })
      , groupOwner: this.LinkCell({
        title: 'Group Owner'
        , target: '_blank'
        , value: (device) => {
          return device.group.owner.name
        }
        , link: (device) => {
          return device.enhancedGroupOwnerProfileUrl
        }
      })
      , groupEndTime: this.TextCell({
        title: 'Group Expiration Date'
        , value: (device) => {
          return device.group.lifeTime.stop, this.settingService.get('dateFormat')
        }
      })
      , groupStartTime: this.TextCell({
        title: 'Group Starting Date'
        , value: (device) => {
          return device.group.lifeTime.start, this.settingService.get('dateFormat')
        }
      })
      , groupRepetitions: this.TextCell({
        title: 'Group Repetitions'
        , value: (device) => {
          return device.group.repetitions
        }
      })
      , groupOrigin: this.TextCell({
        title: 'Group Origin'
        , value: (device) => {
          return device.group.originName
        }
      })
      , model: this.DeviceModelCell({
        title: 'Model'
        , value: (device) => {
          return device.model || device.serial
        }
      })
      , name: this.DeviceNameCell({
        title: 'Product'
        , value: (device) => {
          return device.name || device.model || device.serial
        }
      }, this.currentUser.email)
      , operator: this.TextCell({
        title: 'Carrier'
        , value: (device) => {
          return device.operator != ',' ? device.operator : 'No SIM Available'
        }
      })
      , releasedAt: this.DateCell({
        title: 'Released'
        , value: (device) => {
          return device.releasedAt ? new Date(device.releasedAt) : null
        }
      })
      , version: this.TextCell({
        title: 'OS'
        , value: (device) => {
          return device.version || ''
        }
        , compare: (deviceA, deviceB) => {
          let va = (deviceA.version || '0').split('.')
          let vb = (deviceB.version || '0').split('.')
          let la = va.length
          let lb = vb.length

          for (let i = 0, l = Math.max(la, lb); i < l; ++i) {
            let a = i < la ? parseInt(va[i], 10) : 0
            let b = i < lb ? parseInt(vb[i], 10) : 0
            let diff = a - b

            // One of the values might be something like 'M'. If so, do a string
            // comparison instead.
            if (isNaN(diff)) {
              diff = this.compareRespectCase(va[i], vb[i])
            }

            if (diff !== 0) {
              return diff
            }
          }

          return 0
        }
        , filter: (device: any, filter: any) => {
          let va = (device.version || '0').split('.')
          let vb = (filter.query || '0').split('.')
          let la = va.length
          let lb = vb.length
          let op = this.filterOps[filter.op || '=']

          // We have a single value and no operator or field. It matches
          // too easily, let's wait for a dot (e.g. '5.'). An example of a
          // bad match would be an unquoted query for 'Nexus 5', which targets
          // a very specific device but may easily match every Nexus device
          // as the two terms are handled separately.
          if (filter.op === null && filter.field === null && lb === 1) {
            return false
          }

          if (vb[lb - 1] === '') {
            // This means that the query is not complete yet, and we're
            // looking at something like "4.", which means that the last part
            // should be ignored.
            vb.pop()
            lb -= 1
          }

          for (let i = 0, l = Math.min(la, lb); i < l; ++i) {
            let a = parseInt(va[i], 10)
            let b = parseInt(vb[i], 10)

            // One of the values might be non-numeric, e.g. 'M'. In that case
            // filter by string value instead.
            if (isNaN(a) || isNaN(b)) {
              if (!op(va[i], vb[i])) {
                return false
              }
            }
            else {
              if (!op(a, b)) {
                return false
              }
            }
          }

          return true
        }
      })
      , network: this.TextCell({
        title: 'Network'
        , value: (device) => {
          if (!device.network) {
            return ''
          }

          if (!device.network.connected) {
            return ''
          }

          if (device.network.subtype) {
            return (device.network.type + ' (' + device.network.subtype + ')').toUpperCase()
          }

          return (device.network.type || '').toUpperCase()
        }
      })
      , display: this.TextCell({
        title: 'Screen'
        , defaultOrder: 'desc'
        , value: (device) => {
          return device.display && device.display.width
            ? device.display.width + 'x' + device.display.height
            : ''
        }
        , compare: (deviceA: any, deviceB: any) => {
          let va = deviceA.display && deviceA.display.width
            ? deviceA.display.width * deviceA.display.height
            : 0
          let vb = deviceB.display && deviceB.display.width
            ? deviceB.display.width * deviceB.display.height
            : 0
          return va - vb
        }
      })
      , browser: this.DeviceBrowserCell({
        title: 'Browser'
        , value: (device) => {
          return device.browser || { apps: [] }
        }
      })
      , serial: this.TextCell({
        title: 'Serial'
        , value: (device) => {
          return device.serial || ''
        }
      })
      , manufacturer: this.TextCell({
        title: 'Manufacturer'
        , value: (device) => {
          return device.manufacturer || ''
        }
      })
      , marketName: this.TextCell({
        title: 'Market name'
        , value: (device) => {
          return device.marketName || ''
        }
      })
      , sdk: this.NumberCell({
        title: 'SDK'
        , defaultOrder: 'desc'
        , value: (device) => {
          return device.sdk
        }
        , format: (value) => {
          return value || ''
        }
      })
      , abi: this.TextCell({
        title: 'ABI'
        , value: (device) => {
          return device.abi || ''
        }
      })
      , cpuPlatform: this.TextCell({
        title: 'CPU Platform'
        , value: (device) => {
          return device.cpuPlatform || ''
        }
      })
      , openGLESVersion: this.TextCell({
        title: 'OpenGL ES version'
        , value: (device) => {
          return device.openGLESVersion || ''
        }
      })
      , phone: this.TextCell({
        title: 'Phone'
        , value: (device) => {
          return device.phone ? device.phone.phoneNumber : ''
        }
      })
      , imei: this.TextCell({
        title: 'Phone IMEI'
        , value: (device) => {
          return device.phone ? device.phone.imei : ''
        }
      })
      , imsi: this.TextCell({
        title: 'Phone IMSI'
        , value: (device) => {
          return device.phone ? device.phone.imsi : ''
        }
      })
      , iccid: this.TextCell({
        title: 'Phone ICCID'
        , value: (device) => {
          return device.phone ? device.phone.iccid : ''
        }
      })
      , batteryHealth: this.TextCell({
        title: 'Battery Health'
        , value: (device) => {
          return device.battery
            ? device.enhancedBatteryHealth
            : ''
        }
      })
      , batterySource: this.TextCell({
        title: 'Battery Source'
        , value: (device) => {
          return device.battery
            ? device.enhancedBatterySource
            : ''
        }
      })
      , batteryStatus: this.TextCell({
        title: 'Battery Status'
        , value: (device) => {
          return device.battery
            ? device.enhancedBatteryStatus
            : ''
        }
      })
      , batteryLevel: this.NumberCell({
        title: 'Battery Level'
        , value: (device) => {
          return device.battery
            ? Math.floor(device.battery.level / device.battery.scale * 100)
            : null
        }
        , format: (value) => {
          return value === null ? '' : value + '%'
        }
      })
      , batteryTemp: this.NumberCell({
        title: 'Battery Temp'
        , value: (device) => {
          return device.battery ? device.battery.temp : null
        }
        , format: (value) => {
          return value === null ? '' : value + '°C'
        }
      })
      , provider: this.TextCell({
        title: 'Location'
        , value: (device) => {
          return device.provider ? device.provider.name : ''
        }
      })
      , notes: this.DeviceNoteCell({
        title: 'Notes'
        , value: (device) => {
          return device.notes || ''
        }
      })
      , owner: this.LinkCell({
        title: 'User'
        , target: '_blank'
        , value: (device) => {
          return device.owner ? device.owner.name : ''
        }
        , link: (device) => {
          return device.owner ? device.enhancedUserProfileUrl : ''
        }
      })
    };

  }

  zeroPadTwoDigit(digit: any) {
    return digit < 10 ? '0' + digit : '' + digit
  }

  compareIgnoreCase(a: any, b: any) {
    /***** fix bug: cast to String for Safari compatibility ****/
    let la = (String(a) || '').toLowerCase()
    let lb = (String(b) || '').toLowerCase()
    /***********************************************************/
    if (la === lb) {
      return 0
    }
    else {
      return la < lb ? -1 : 1
    }
  }



  compareRespectCase(a: any, b: any) {
    if (a === b) {
      return 0
    }
    else {
      return a < b ? -1 : 1
    }
  }

  filterIgnoreCase(a: any, filterValue: any) {

    /***** fix bug: cast to String for Safari compatibility ****/
    let va = (String(a) || '').toLowerCase()
    let vb = String(filterValue).toLowerCase()


    // /***********************************************************/
    return va.indexOf(vb) !== -1
  }

  TextCell(options: any) {



    return _.defaults(options, {
      title: options.title
      , defaultOrder: 'asc'
      , build: () => {
        let td = document.createElement('td')
        td.appendChild(document.createTextNode(''))
        return td
      }
      , update: (td: any, item: any) => {
        let t = td.firstChild
        t.nodeValue = options.value(item)
        return td
      }
      , compare: (a: any, b: any) => {
        return this.compareIgnoreCase(options.value(a), options.value(b))
      }
      , filter: (item: any, filter: any) => {





        return this.filterIgnoreCase(options.value(item), filter.query)
      }
    })
  }


  NumberCell(options: any) {
    return _.defaults(options, {
      title: options.title
      , defaultOrder: 'asc'
      , build: () => {
        let td = document.createElement('td')
        td.appendChild(document.createTextNode(''))
        return td
      }
      , update: (td: any, item: any) => {
        let t = td.firstChild
        t.nodeValue = options.format(options.value(item))
        return td
      }
      , compare: (a: any, b: any) => {
        let va = options.value(a) || 0
        let vb = options.value(b) || 0
        return va - vb
      }
      , filter: (() => {
        return (item: any, filter: any) => {
          return this.filterOps[filter.op || '='](
            options.value(item)
            , Number(filter.query)
          )
        }
      })
        ()
    })
  }

  DateCell(options: any) {
    return _.defaults(options, {
      title: options.title
      , defaultOrder: 'desc'
      , build: () => {
        let td = document.createElement('td')
        td.appendChild(document.createTextNode(''))
        return td
      }
      , update: (td: any, item: any) => {
        let t = td.firstChild
        let date = options.value(item)
        if (date) {
          t.nodeValue = date.getFullYear()
            + '-'
            + this.zeroPadTwoDigit(date.getMonth() + 1)
            + '-'
            + this.zeroPadTwoDigit(date.getDate())
        }
        else {
          t.nodeValue = ''
        }
        return td
      }
      , compare: (a: any, b: any) => {
        let va = options.value(a) || 0
        let vb = options.value(b) || 0
        return va - vb
      }
      , filter: (() => {
        function dateNumber(d: any) {
          return d
            ? d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate()
            : 0
        }
        return (item: any, filter: any) => {
          let filterDate = new Date(filter.query)
          let va = dateNumber(options.value(item))
          let vb = dateNumber(filterDate)
          return this.filterOps[filter.op || '='](va, vb)
        }
      })()
    })
  }

  LinkCell(options: any) {
    return _.defaults(options, {
      title: options.title
      , defaultOrder: 'asc'
      , build: () => {
        let td = document.createElement('td')
        let a = document.createElement('a')
        a.appendChild(document.createTextNode(''))
        td.appendChild(a)
        return td
      }
      , update: (td: any, item: any) => {
        let a = td.firstChild
        let t = a.firstChild
        let href = options.link(item)
        if (href) {
          a.setAttribute('href', href)
        }
        else {
          a.removeAttribute('href')
        }
        a.target = options.target || ''
        t.nodeValue = options.value(item)
        return td
      }
      , compare: (a: any, b: any) => {
        return this.compareIgnoreCase(options.value(a), options.value(b))
      }
      , filter: (item: any, filter: any) => {
        return this.filterIgnoreCase(options.value(item), filter.query)
      }
    })
  }

  DeviceBrowserCell(options: any) {
    return _.defaults(options, {
      title: options.title
      , defaultOrder: 'asc'
      , build: () => {
        let td = document.createElement('td')
        let span = document.createElement('span')
        span.className = 'device-browser-list'
        span.setAttribute('style', 'display: flex; flex-direction: column;');
        td.appendChild(span)
        return td
      }
      , update: (td, device) => {
        let span = td.firstChild
        let browser = options.value(device)
        let apps = browser.apps.slice().sort((appA, appB) => {
          return this.compareIgnoreCase(appA.name, appB.name)
        })

        for (let i = 0, l = apps.length; i < l; ++i) {
          let app = apps[i]
          let img = span.childNodes[i] || span.appendChild(document.createElement('img'))
          let src = '/assets/browsers/' + (app.type || '_default') + '.png'

          // Only change if necessary so that we don't trigger a download
          if (img.getAttribute('src') !== src) {
            img.setAttribute('src', src)
          }
          img.setAttribute('style', "width:18px;height:auto;");
          img.setAttribute('alt', app.name);

          img.title = app.name + ' (' + app.developer + ')'
        }

        while (span.childNodes.length > browser.apps.length) {
          span.removeChild(span.lastChild)
        }

        return td
      }
      , compare: (a, b) => {
        return options.value(a).apps.length - options.value(b).apps.length
      }
      , filter: (device: any, filter: any) => {
        return options.value(device).apps.some((app) => {
          return this.filterIgnoreCase(app.type, filter.query)
        })
      }
    })
  }

  DeviceModelCell(options) {
    return _.defaults(options, {
      title: options.title
      , defaultOrder: 'asc'
      , build: () => {
        let td = document.createElement('td')
        let span = document.createElement('span')
        let image = document.createElement('img')
        span.className = 'device-small-image'
        image.className = 'device-small-image-img'
        image.role = "button"
        span.appendChild(image)
        span.appendChild(document.createTextNode("\u00A0"))
        td.appendChild(span)
        td.appendChild(document.createTextNode(''))
        return td
      }
      , update: (td, device) => {
        let span = td.firstChild
        let image = span.firstChild
        let t = span.nextSibling
        let src = 'assets/x24/_default.jpg'
        // +
        // (device.image || '_default.jpg')
        // Only change if necessary so that we don't trigger a download
        if (image.getAttribute('src') !== src) {
          image.setAttribute('src', src)
        }

        t.nodeValue = options.value(device)

        return td
      }
      , compare: (a, b) => {
        return this.compareRespectCase(options.value(a), options.value(b))
      }
      , filter: (device: any, filter: any) => {


        return this.filterIgnoreCase(options.value(device), filter.query)
      }
    })
  }

  // DeviceNameCell(options, ownerEmail) {
  //   return _.defaults(options, {
  //     title: options.title
  //     , defaultOrder: 'asc'
  //     , build: function () {
  //       let td = document.createElement('td')
  //       let a = document.createElement('a')
  //       a.appendChild(document.createTextNode(''))
  //       td.appendChild(a)
  //       return td
  //     }
  //     , update: function (td, device) {
  //       let a = td.firstChild
  //       let t = a.firstChild

  //       if (device.using && device.owner.email === ownerEmail) {
  //         a.className = 'device-product-name-using'
  //         //a.href = '#!/control/' + device.serial
  //       }
  //       else if (device.usable && !device.using) {
  //         a.className = 'device-product-name-usable'
  //         // a.href = '#!/control/' + device.serial
  //       }
  //       else {
  //         a.className = 'device-product-name-unusable'
  //         a.removeAttribute('href')
  //       }

  //       t.nodeValue = options.value(device)

  //       return td
  //     }
  //     , compare: function (a, b) {
  //       return this.compareIgnoreCase(options.value(a), options.value(b))
  //     }
  //     , filter: function (device, filter) {
  //       return this.filterIgnoreCase(options.value(device), filter.query)
  //     }
  //   })
  // }
  DeviceNameCell(options: any, ownerEmail: any) {
    const title = options.title;
    const defaultOrder = 'asc';

    const build = () => {
      const td = document.createElement('td');
      const a = document.createElement('a');
      a.appendChild(document.createTextNode(''));
      td.appendChild(a);
      return td;
    };

    const update = (td: any, device: any) => {
      const a = td.firstChild;
      const t = a.firstChild;

      if (device.using && device.owner?.email === ownerEmail) {
        a.className = 'device-product-name-using text-decoration-none';
        //a.href = '#!/control/' + device.serial
      } else if (device.usable && !device.using) {
        a.className = 'device-product-name-usable text-decoration-none';
        // a.href = '#!/control/' + device.serial
      } else {
        a.className = 'device-product-name-unusable text-decoration-none text-secondary';
        a.removeAttribute('href');
      }

      t.nodeValue = options.value(device);

      return td;
    };

    const compare = (a: any, b: any) => {
      return this.compareIgnoreCase(options.value(a), options.value(b));
    };

    const filter = (device: any, filter: any) => {


      return this.filterIgnoreCase(options.value(device), filter.query);
    };

    return _.defaults(options, {
      title,
      defaultOrder,
      build,
      update,
      compare,
      filter
    });
  }

  DeviceStatusCell(options: any): any {
    const stateClasses = {
      using: 'state-using btn-primary btn-sm',
      busy: 'state-busy btn-warning btn-sm',
      available: 'state-available btn-outline-primary btn-sm',
      ready: 'state-ready btn-outline-primary btn-sm',
      present: 'state-present btn-outline-primary btn-sm',
      preparing: 'state-preparing btn-outline-primary btn-outline-success btn-sm',
      unauthorized: 'state-unauthorized btn-outline-danger btn-sm',
      offline: 'state-offline btn-outline-warning btn-sm',
      automation: 'state-automation btn-info btn-sm'
    };

    return {
      ...options,
      defaultOrder: 'asc',
      build: () => {
        const td = document.createElement('td');
        const a = document.createElement('a');
        a.appendChild(document.createTextNode(''));
        td.appendChild(a);
        return td;
      },
      update: (td: HTMLElement, device: any) => {
        const a = td.firstChild as HTMLAnchorElement;
        const t = a.firstChild as Text;

        a.className = 'btn btn-xs device-status ' + (stateClasses[device.state] || 'btn-light text-secondary btn-sm');
        // Apply custom styling based on enhancedStateAction and owner
        if (device.enhancedStateAction === "Stop Using") {
          if (device.owner?.name !== "admin") {
            if (this.currentUser.userObj.privilege == "admin") {
              a.style.backgroundColor = '#ff6426';
              a.style.color = 'Black';
            } else {
              a.style.backgroundColor = '#167ffc';
              a.style.color = 'White';
            }
          } else {
            a.style.backgroundColor = '#167ffc';
            a.style.color = 'White';
          }
        } else {
          a.style.backgroundColor = 'aliceblue';
          a.style.color = 'Black';
        }

        if (device.usable && !device.using) {


          // a.href = '#!/control/' + device.serial;
        } else {
          a.removeAttribute('href');
        }

        t.nodeValue = options.value(device);

        return td;
      },
      compare: (() => {
        const order = {
          using: 10,
          automation: 15,
          available: 20,
          busy: 30,
          ready: 40,
          preparing: 50,
          unauthorized: 60,
          offline: 70,
          present: 80,
          absent: 90
        };
        return (deviceA: any, deviceB: any) => {
          return order[deviceA.state] - order[deviceB.state];
        };
      })(),
      filter: (device: any, filter: any) => {


        return device.state === filter.query;
      }
    };
  }


  DeviceNoteCell(options) {
    return _.defaults(options, {
      title: options.title
      , defaultOrder: 'asc'
      , build: () => {
        let td = document.createElement('td')
        let span = document.createElement('span')
        let i = document.createElement('i')

        td.className = 'device-note'
        span.className = 'xeditable-wrapper'
        span.appendChild(document.createTextNode(''))

        i.className = 'device-note-edit fa fa-pencil pointer'
        i.role = "button"

        td.appendChild(span)
        td.appendChild(i)

        return td
      }
      , update: (td, item) => {
        let span = td.firstChild
        let t = span.firstChild

        t.nodeValue = options.value(item)
        return td
      }
      , compare: (a, b) => {
        return this.compareIgnoreCase(options.value(a), options.value(b))
      }
      , filter: (item: any, filter: any) => {
        return this.filterIgnoreCase(options.value(item), filter.query)
      }
    })
  }
}
